'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useLanguage } from '@/contexts/LanguageContext';

interface Benefit {
  id: string;
  text: string;
  icon: string;
}

interface CategoryDiscount {
  category: string;
  range: string;
  icon: string;
}

interface SubscriptionCardProps {
  type: 'VIP' | 'Family' | 'Family3Month' | 'Personal' | 'Corporate';
  price: number;
  capacity: number;
  cardImage: string;
  benefits: Benefit[];
  discounts: CategoryDiscount[];
  isPopular?: boolean;
  duration?: string;
  onSubscribe: () => void;
}

const SubscriptionCard = ({
  type,
  price,
  capacity,
  cardImage,
  benefits,
  discounts,
  isPopular = false,
  duration = 'سنة كاملة',
  onSubscribe,
}: SubscriptionCardProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const { t } = useLanguage();

  const getCardTitle = () => {
    if (type === 'VIP') return t('جودة الحياة VIP', 'Quality of Life VIP');
    if (type === 'Personal') return t('جودة الحياة شخصي', 'Quality of Life Personal');
    if (type === 'Family3Month') return t('جودة الحياة عائلي ربع سنوي', 'Quality of Life Family Quarterly');
    if (type === 'Corporate') return t('جودة الحياة شركات', 'Quality of Life Corporate');
    return t('جودة الحياة عائلي', 'Quality of Life Family');
  };

  const getDuration = () => {
    if (duration === 'سنة كاملة') return t('سنة كاملة', 'Full Year');
    if (duration === '3 أشهر فقط') return t('3 أشهر فقط', '3 Months Only');
    return duration;
  };

  const corporateFeatures = [
    { ar: 'خصومات على المطاعم', en: 'Restaurant discounts' },
    { ar: 'خصومات على العيادات الطبية', en: 'Medical clinic discounts' },
    { ar: 'خصومات على الملابس', en: 'Clothing discounts' },
    { ar: 'خصومات على الحلويات', en: 'Sweets discounts' },
    { ar: 'خصومات على النوادي الرياضية', en: 'Sports club discounts' },
    { ar: 'عدد مرن من الموظفين', en: 'Flexible number of employees' },
    { ar: 'عروض حصرية شهرية', en: 'Monthly exclusive offers' },
    { ar: 'أولوية في الحجوزات', en: 'Priority bookings' },
    { ar: 'دعم فني على مدار الساعة', en: '24/7 technical support' },
  ];

  return (
    <div
      className={`relative bg-card rounded-xl shadow-lg border-2 transition-premium hover:shadow-xl ${
        isPopular ? 'border-accent' : 'border-border'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground px-6 py-2 rounded-full text-sm font-bold shadow-md">
          {t('الأكثر شعبية', 'Most Popular')}
        </div>
      )}

      <div className="p-6 md:p-8">
        {/* Card Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl md:text-3xl font-bold text-primary font-amiri mb-2">
            {getCardTitle()}
          </h3>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Icon name="UsersIcon" size={20} />
            {type === 'Corporate' ? (
              <span className="text-sm">{t('عدد مرن من الموظفين - 150 ريال / فرد', 'Flexible employees - 150 SAR / person')}</span>
            ) : (
              <span className="text-sm">{t('تصلح لـ', 'Valid for')} {capacity} {type === 'Personal' ? t('شخص', 'person') : t('أشخاص', 'people')}</span>
            )}
          </div>
        </div>

        {/* Card Image */}
        <div className="relative w-full h-48 md:h-56 mb-6 rounded-lg overflow-hidden shadow-md">
          <AppImage
            src={cardImage}
            alt={`${getCardTitle()} subscription card`}
            className="w-full h-full object-cover hover-scale"
          />
        </div>

        {/* Pricing */}
        <div className="text-center mb-6 p-4 bg-accent/10 rounded-lg border border-accent/20">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-4xl md:text-5xl font-bold text-accent data-text">
              {type === 'Corporate' ? '150' : price.toLocaleString('en-US')}
            </span>
            <span className="text-xl text-muted-foreground">{type === 'Corporate' ? t('ريال / فرد', 'SAR / person') : t('ريال', 'SAR')}</span>
          </div>
          <p className="text-sm text-muted-foreground">{t('صالحة لمدة', 'Valid for')} {getDuration()}</p>
        </div>

        {/* Corporate Card Features */}
        {type === 'Corporate' && (
          <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
            <h4 className="text-center text-sm font-bold text-primary mb-3">{t('مميزات البطاقة', 'Card Features')}</h4>
            <div className="space-y-2 text-xs">
              {corporateFeatures.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-card rounded">
                  <CheckCircleIcon className="w-4 h-4 text-success flex-shrink-0" />
                  <span className="text-foreground">{t(feature.ar, feature.en)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        <button
          onClick={onSubscribe}
          className="w-full py-4 bg-accent text-accent-foreground rounded-lg font-bold text-lg transition-premium hover:bg-accent/90 hover:shadow-lg active-press flex items-center justify-center gap-2"
        >
          <Icon name="ShoppingCartIcon" size={24} />
          <span>{t('اشترك الآن', 'Subscribe Now')}</span>
        </button>

        {/* Terms Toggle */}
        <button
          onClick={() => setShowTerms(!showTerms)}
          className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-premium flex items-center justify-center gap-2"
        >
          <Icon name="DocumentTextIcon" size={16} />
          <span>{t('الشروط والأحكام', 'Terms & Conditions')}</span>
          <Icon
            name="ChevronDownIcon"
            size={16}
            className={`transition-premium ${showTerms ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Terms Content */}
        {showTerms && (
          <div className="mt-4 p-4 bg-muted rounded-lg text-xs text-muted-foreground space-y-2">
            <p>• {t('البطاقة صالحة لمدة', 'Card is valid for')} {getDuration()} {t('من تاريخ التفعيل', 'from activation date')}</p>
            {type === 'Corporate' ? (
              <>
                <p>• {t('عدد الموظفين مرن ويحدده صاحب الشركة', 'Number of employees is flexible and determined by the company owner')}</p>
                <p>• {t('سعر الفرد 150 ريال سنوياً', 'Price per person is 150 SAR annually')}</p>
              </>
            ) : (
              <p>• {t('يمكن استخدام البطاقة من قبل', 'Card can be used by')} {capacity} {t('أشخاص فقط', 'people only')}</p>
            )}
            <p>• {t('الخصومات تختلف حسب الشريك والفئة', 'Discounts vary by partner and category')}</p>
            <p>• {t('لا يمكن استرجاع قيمة الاشتراك بعد التفعيل', 'Subscription value cannot be refunded after activation')}</p>
            <p>• {t('يجب إبراز البطاقة عند الاستفادة من الخصم', 'Card must be presented when using the discount')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;